import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { clearChurchState, createChurchDemoState, isChurchEmpty } from '@escala/domain/churchDemo';
import {
  createOrgState,
  createSampleState,
  DEMO_ADMIN_ID,
  DEMO_LEADER_ID,
  DEMO_ORG_ADMIN_ID,
  DEMO_ROLE_LEADER_ID,
  DEMO_MEMBER_ID,
  SAMPLE_ORG,
} from '@escala/domain/seed';
import { formatDayMonth, normalize, todayISO } from '@escala/domain/dates';
import {
  deactivateMember,
  deactivationError,
  deactivationImpact,
  isActiveMember,
  reactivateMember,
} from '@escala/domain/membership';
import {
  applyMemberEdits,
  memberEditError,
  revertEdits,
  type ImportPlan,
  type MemberEdit,
} from '@escala/domain/memberBulk';
import {
  generateSchedule,
  type GenerationOptions,
  type GenerationResult,
  type ProposalAdjustment,
} from '@escala/domain/generator';
import { type EditOptions, editImpact } from '@escala/domain/eventEdit';
import {
  backfillEvents,
  clearServed,
  copyServedFromPrevious,
  setRecorded,
  setServed,
} from '@escala/domain/history';
import { seriesEndPlan } from '@escala/domain/eventSeries';
import { addOccurrence, occurrenceError } from '@escala/domain/occurrences';
import { EMPTY_ADDRESS, EMPTY_PROFILE } from '@escala/domain/members';
import { photoDataUrl } from '@escala/domain/photo';
import {
  type TemplateDraft,
  roleFromSeed,
  roleSeeds,
  templateDraftError,
} from '@escala/domain/ministryTemplates';
import {
  PLATFORM_ADMIN_LABEL,
  ROLE_LABEL,
  permissionsOf,
  type Permissions,
} from '@escala/domain/permissions';
import {
  type PrivacyResolution,
  privacyRequestError,
  resolutionError,
  resolvePrivacyRequest,
  submitPrivacyRequest,
} from '@escala/domain/privacy';
import { mergeError } from '@escala/domain/registrationMerge';
import { approvalError } from '@escala/domain/registrations';
import * as V from '@escala/domain/visitors';
import { APP_HOST } from '@escala/domain/orgs';
import { PROFILE_LINK_DAYS, canShareProfileLink, profileLinkPath } from '@escala/domain/profileLink';
import {
  cancellationAudience,
  restoreSkipped,
  restoreSkippedError,
  skipOccurrence,
  skipsDate,
} from '@escala/domain/skippedDates';
import { type AudienceInput, audienceInputError, audienceOpen } from '@escala/domain/audience';
import { canRetract, presenceSummary } from '@escala/domain/presence';
import { buildLookup, type Lookup } from '@escala/domain/schedule';
import { type SwapCandidateState, swapCandidates } from '@escala/domain/swaps';
import type {
  AccessStatus,
  AccountPrivacy,
  AppRole,
  Attendance,
  DomainState,
  LogEntry,
  Member,
  Ministry,
  Organization,
  PrivacyRequestKind,
  ResponseStatus,
  ScheduleResponse,
  Role,
  ScheduleEvent,
  Session,
  Settings,
  ContactOutcome,
  FollowUpKind,
  FollowUpStatus,
  VisitorVisit,
  MergeField,
} from '@escala/domain/types';

import type { OrgCreate, PhotoUpload } from '@escala/contracts';
import type { Api } from '@/services/api/escala';

import { apiAccounts, type AccountsClient } from './accounts';
import { apiCalendar, type CalendarClient } from './calendar';
import { isDeepStrictEqual } from './equal';
import { apiPrivacy, type PrivacyClient } from './privacy';
import { apiOrgs, type OrgsClient } from './orgs';
import { createRemote, problemMessage, type Remote } from './remote';
import { describeRoleChange } from './roleChange';
import * as T from '@escala/domain/transitions';

/**
 * Store da aplicação: o estado do domínio, quem está usando, o desfazer da edição de
 * escala, o histórico de alterações e o aviso (toast).
 *
 * Cada ação compõe uma transição pura de `@escala/domain/transitions` e muda a tela na
 * hora. Com a API (prop `api`), a sessão é a do login, o estado inicial vem de `GET /state`
 * e cada ação é repetida na API, em fila (`remote.ts`). Sem ela — só nos testes —, a store
 * fica em memória, com o perfil de `initialRole` e os clientes de `memory`
 * (`test/memory`). As telas só conhecem `useStore()`.
 */

/** Colega para "Pedir troca", com a situação dele na data (sem a disponibilidade). */
export interface SwapOption {
  memberId: string;
  state: SwapCandidateState;
  /** Indisponível ou ausente: aparece, mas não pode ser escolhido. */
  blocked: boolean;
}

export interface Toast {
  id: number;
  message: string;
  /**
   * "Desfazer" no aviso: `true` desfaz a última edição de escala (a pilha de desfazer); uma
   * função desfaz a ação que o aviso confirma (a resposta do membro, a data marcada).
   */
  undo: boolean | (() => void);
  /** `success` confirma que deu certo; `error`, que não deu (com o motivo); `info`, o resto. */
  tone: ToastTone;
}

export type ToastTone = 'success' | 'error' | 'info';

export type EditChoices = Pick<EditOptions, 'includeScheduled' | 'includePastEmpty'>;

/**
 * Uma edição de escala que dá para desfazer. Guarda a lista de antes e, dos eventos que a
 * edição tocou, como ficaram (`null` = excluído): desfazer volta só esses, e só se ninguém
 * mexeu neles depois — o resto (respostas, check-in, trocas, outros eventos) fica.
 */
interface UndoEntry {
  events: ScheduleEvent[];
  after: Map<string, ScheduleEvent | null>;
  eventIds: string[];
  message: string;
}

const UNDO_LIMIT = 30;

/** Os eventos que a edição mudou, criou (o novo) ou excluiu (`null`). */
function touchedEvents(before: ScheduleEvent[], after: ScheduleEvent[]): Map<string, ScheduleEvent | null> {
  const was = new Map(before.map((e) => [e.id, e]));
  const touched = new Map<string, ScheduleEvent | null>();

  for (const event of after) {
    if (was.get(event.id) !== event) touched.set(event.id, event);
    was.delete(event.id);
  }
  for (const id of was.keys()) touched.set(id, null);

  return touched;
}

/**
 * A lista com os eventos da edição de volta como eram — ou `null`, se algum deles mudou
 * depois por fora da pilha (resposta, check-in, troca): desfazer apagaria essa mudança.
 */
function undoEvents(current: ScheduleEvent[], entry: UndoEntry): ScheduleEvent[] | null {
  const now = new Map(current.map((e) => [e.id, e]));

  for (const [id, after] of entry.after) {
    if (!isDeepStrictEqual(now.get(id) ?? null, after)) return null;
  }

  const restored = entry.events.map((e) => (entry.after.has(e.id) ? e : (now.get(e.id) ?? e)));
  const kept = new Set(restored.map((e) => e.id));

  // Evento que nasceu depois por outro caminho (não deveria haver) continua na lista.
  return [...restored, ...current.filter((e) => !kept.has(e.id) && !entry.after.has(e.id))];
}
const TOAST_MS = 5000;

/**
 * O perfil de quem usa: a plataforma ou um perfil da organização. Com a API, o do login;
 * na store em memória (testes), escolhe o cadastro dos dados de exemplo — o Administrador
 * da plataforma (Marcos), o da organização (Daniel), os dois líderes e o membro.
 */
export type Persona = 'platform' | AppRole;

export const PERSONA_LABEL: Record<Persona, string> = { platform: PLATFORM_ADMIN_LABEL, ...ROLE_LABEL };

const DEMO_MEMBER_OF: Record<Persona, string> = {
  platform: DEMO_ADMIN_ID,
  admin: DEMO_ORG_ADMIN_ID,
  leader: DEMO_LEADER_ID,
  roleLeader: DEMO_ROLE_LEADER_ID,
  member: DEMO_MEMBER_ID,
};

/** As organizações da store em memória (testes): a aberta e o estado guardado das demais. */
interface MemoryWorld {
  orgs: Organization[];
  orgId: string;
  stash: Record<string, DomainState>;
  /** Administrador de cada organização criada. */
  admins: Record<string, string>;
}

const SAMPLE_WORLD: MemoryWorld = { orgs: [SAMPLE_ORG], orgId: SAMPLE_ORG.id, stash: {}, admins: {} };

/** Os perfis que existem na organização: numa criada em memória, só os administradores. */
function personasOf(world: Pick<MemoryWorld, 'orgId' | 'admins'>): Persona[] {
  if (world.orgId === SAMPLE_ORG.id) return ['platform', 'admin', 'leader', 'roleLeader', 'member'];

  return world.admins[world.orgId] ? ['platform', 'admin'] : ['platform'];
}

/**
 * Sem a API (só nos testes), a store guarda o estado em memória e os clientes de acessos,
 * privacidade, organizações e agenda vêm de `memory` (`test/memory`), lendo daqui.
 */
export interface MemorySource {
  members: () => readonly Member[];
  ministries: () => readonly Ministry[];
  me: () => Member;
  state: () => DomainState;
  org: () => Organization;
  orgs: () => readonly Organization[];
  stateOf: (orgId: string) => DomainState | undefined;
  /** O administrador da organização e a situação do acesso dele. */
  adminOf: (orgId: string) => { memberId: string; status: AccessStatus } | null;
  /** Cria a organização com o administrador (convidado) e, se pedido, os ministérios sugeridos. */
  addOrg: (input: OrgCreate) => Organization;
  putOrg: (org: Organization) => void;
}

export interface MemoryClients {
  accounts: AccountsClient;
  privacy: PrivacyClient;
  orgs: OrgsClient;
  calendar: CalendarClient;
}

/** A sessão de um perfil: a plataforma é Administrador em qualquer organização. */
function sessionOf(persona: Persona, memberId: string): Session {
  return persona === 'platform'
    ? { role: 'admin', memberId, platformAdmin: true }
    : { role: persona, memberId };
}

/**
 * Quem está na sessão sem ser membro da organização aberta (o Administrador da plataforma
 * dentro de outra igreja): a store o representa com este cadastro, fora da lista.
 */
function guestMember(id: string, name: string, email: string): Member {
  return {
    id,
    name,
    email,
    nickname: '',
    selfUpdatedAt: null,
    phone: '',
    birthDate: null,
    sex: 'M',
    photo: null,
    spouseId: null,
    spouseText: '',
    coupleMode: null,
    roles: {},
    availability: Array(21).fill('unset'),
    blackouts: [],
    ...EMPTY_PROFILE,
    inactiveSince: null,
    // A plataforma é Administradora em qualquer organização em que entra.
    admin: true,
  };
}

export interface StoreActions {
  /** Troca a organização aberta (Administrador da plataforma): volta ao Dashboard dela. */
  switchOrg: (orgId: string) => void;
  /** Edita os dados da organização aberta (card de Configurações). */
  updateOrg: (
    patch: Partial<Pick<Organization, 'name' | 'slug' | 'city' | 'uf' | 'address' | 'color'>>
  ) => Promise<void>;
  /** "Aprovar e incluir": cria o membro; devolve o motivo quando não dá (nome ou e-mail repetido). */
  approveRegistration: (registrationId: string) => string | null;
  mergeRegistration: (registrationId: string, memberId: string, fields: MergeField[]) => string | null;
  rejectRegistration: (registrationId: string, reason: string) => void;
  /** "Gerar novo link": o anterior para de funcionar. */
  rotateRegistrationLink: () => Promise<void>;
  /** Pausa ou retoma os cadastros pelo link. */
  setRegistrationLinkActive: (active: boolean) => void;
  /**
   * Visitantes (Membresia): nova ficha (sem `id`) ou edição; devolve o id gravado ou o
   * motivo (`{ error }`) quando não dá.
   */
  saveVisitor: (draft: V.VisitorDraft, id?: string) => { id: string } | { error: string };
  recordVisitorVisit: (visitorId: string, visit: VisitorVisit) => void;
  moveVisitor: (visitorId: string, stage: string) => void;
  assignVisitorOwner: (visitorId: string, ownerId: string | null) => void;
  /** Novo acompanhamento; devolve o motivo quando não dá. */
  addVisitorFollowUp: (visitorId: string, draft: V.FollowUpDraft) => string | null;
  updateVisitorFollowUp: (
    visitorId: string,
    followUpId: string,
    patch: Partial<V.FollowUpDraft & { status: FollowUpStatus }>
  ) => void;
  logVisitorContact: (visitorId: string, kind: FollowUpKind, outcome: ContactOutcome) => void;
  /** "Encaminhar para o cadastro"; devolve o motivo quando não dá. */
  referVisitor: (visitorId: string) => string | null;
  /** Liga a ficha ao cadastro que a pessoa fez pelo link (a sugestão em Cadastros). */
  linkVisitorRegistration: (visitorId: string, registrationId: string) => void;
  anonymizeVisitor: (visitorId: string) => void;
  rotateVisitorLink: () => Promise<void>;
  setVisitorLinkActive: (active: boolean) => void;
  /** Pedido do próprio titular (LGPD); devolve o motivo quando não dá. */
  submitPrivacyRequest: (kind: PrivacyRequestKind, note: string) => string | null;
  /** Resposta do Administrador (na exclusão, pode anonimizar); devolve o motivo quando não dá. */
  resolvePrivacyRequest: (requestId: string, resolution: PrivacyResolution) => string | null;
  showToast: (message: string, options?: { undo?: Toast['undo']; tone?: ToastTone }) => void;
  hideToast: () => void;
  undo: () => void;
  assign: (eventId: string, roleId: string, memberId: string, replaceId?: string | null) => void;
  remove: (eventId: string, roleId: string, memberId: string) => void;
  move: (eventId: string, fromRoleId: string, toRoleId: string, memberId: string) => void;
  publish: (eventId: string) => void;
  applyGeneration: (
    result: GenerationResult,
    publish: boolean,
    adjustments?: readonly ProposalAdjustment[],
    adjusted?: GenerationResult
  ) => void;
  createEvents: (draft: T.EventDraft) => string;
  /** Ocorrências passadas de uma série semanal; devolve quantas foram criadas. */
  backfill: (seriesId: string, since: string) => number;
  /** Nova data de um evento único que já existe; devolve o motivo quando não dá. */
  addOccurrence: (seriesId: string, date: string, time: string) => string | null;
  /** Escalas › Histórico: marca ou desmarca quem serviu (salvo na hora, sem aviso). */
  setServed: (eventId: string, roleId: string, memberId: string, served: boolean) => void;
  /** "Copiar do evento anterior" nas funções que o perfil edita; `false` sem data anterior. */
  copyServedFromPrevious: (eventId: string) => boolean;
  clearServed: (eventId: string) => void;
  /** "Concluir" (`true`) ou "Reabrir" (`false`) o registro da data. */
  setRecorded: (eventId: string, recorded: boolean) => void;
  /**
   * Edita uma data ou a série. As escolhas da confirmação dizem se as datas futuras com
   * escala e as passadas sem registro recebem a edição; as passadas com quem serviu nunca
   * perdem ninguém (na série, só o título).
   */
  updateEvents: (draft: T.EventDraft, choices?: EditChoices) => void;
  /**
   * Exclui esta data, esta e as próximas ou a série. "Só esta data" numa série que se repete
   * a guarda como excluída, com o motivo (`skipOccurrence`), e avisa quem estava escalado.
   */
  deleteEvent: (event: ScheduleEvent, scope: T.EventScope, reason?: string) => void;
  /** Traz de volta uma data excluída da série, sem escala; o motivo quando não dá. */
  restoreSkipped: (seriesId: string, date: string) => string | null;
  saveMinistry: (draft: T.MinistryDraft) => { ok: true; id: string } | { ok: false; error: string };
  /** "Usar modelo": cria o ministério já com as funções marcadas no painel. */
  createFromTemplate: (draft: TemplateDraft) => { ok: true; id: string } | { ok: false; error: string };
  deleteMinistry: (ministryId: string) => void;
  addRole: (ministryId: string) => string;
  updateRole: (ministryId: string, roleId: string, patch: Partial<Omit<Role, 'id'>>) => void;
  deleteRole: (ministryId: string, roleId: string) => void;
  enableInRole: (memberId: string, ministryId: string, roleId: string) => void;
  /** Tira o membro da função (habilitação, liderança, treinamento, pares) e das escalas de hoje em diante. */
  disableInRole: (memberId: string, ministryId: string, roleId: string) => void;
  /** Membro novo, ainda fora da lista: o perfil o edita como rascunho até "Cadastrar". */
  newMemberDraft: () => Member;
  /** Grava o cadastro inteiro (novo ou existente) de uma vez, como o Salvar do perfil. */
  saveMember: (member: Member) => void;
  /**
   * Envia (a foto já recortada e reduzida) ou remove (`null`) a foto do membro, na hora —
   * fora do rascunho do perfil. No modo API espera o servidor; devolve se gravou.
   */
  setMemberPhoto: (memberId: string, photo: PhotoUpload | null) => Promise<boolean>;
  /**
   * Inativa (tira dos ministérios e das escalas de hoje em diante, com o histórico de cada
   * evento; no modo API, o login dele para) ou reativa o membro. Só o Administrador da
   * organização; a pilha do desfazer é limpa ao inativar. Devolve o motivo quando não dá.
   */
  setMemberInactive: (memberId: string, inactive: boolean) => string | null;
  /**
   * Grava a importação: cria os novos e completa quem já estava cadastrado (o que a
   * planilha trouxe onde o cadastro estava vazio, mais as funções e a disponibilidade da
   * etapa "Atribuir"). Só o Administrador. Devolve o motivo quando não dá.
   */
  importMembers: (plan: ImportPlan) => string | null;
  /** A semente dos ids da importação aberta (`importPlan`). */
  importSeed: () => string;
  /**
   * O link pessoal para o membro completar o cadastro (o endereço inteiro e até quando
   * vale), ou o motivo quando não dá. Na store em memória (testes), um endereço fictício.
   */
  profileLink: (memberId: string) => Promise<{ url: string; expiresAt: string } | { error: string }>;
  /**
   * Funções e disponibilidade de vários membros de uma vez (o modo Editor de Membros), com
   * as regras do Salvar do perfil e "Desfazer" no aviso. Devolve o motivo quando não dá.
   */
  bulkEditMembers: (edits: readonly MemberEdit[]) => string | null;
  updateSettings: (patch: Partial<Settings>) => void;
  /** O próprio membro confirma ou avisa que não pode ir (a recusa reabre a vaga). */
  respond: (eventId: string, status: ResponseStatus, reason?: string) => void;
  /** Os colegas para "Pedir troca" na escala do próprio membro (no modo API, a situação vem do servidor). */
  swapCandidates: (eventId: string) => Promise<SwapOption[]>;
  /** "Pedir troca": o próprio escalado pede ao colega que assuma a sua função. */
  requestSwap: (eventId: string, toId: string) => void;
  /** Quem pediu desiste do pedido em aberto. */
  cancelSwap: (eventId: string, swapId: string) => void;
  /** O colega que recebeu aceita (vai ao líder) ou recusa. */
  answerSwap: (eventId: string, swapId: string, accept: boolean) => void;
  /** O líder da função aprova (a escala muda) ou recusa a troca. */
  decideSwap: (eventId: string, swapId: string, approve: boolean) => void;
  /** Check-in do dia, nas funções que o perfil edita. */
  recordAttendance: (eventId: string, marks: Record<string, Attendance>) => void;
  /** Cobra os pendentes das funções que o perfil edita. */
  nudge: (eventId: string) => void;
  /**
   * Registra (ou edita) o público do culto — Administrador, no dia do culto ou depois.
   * Devolve o motivo quando não dá.
   */
  recordAudience: (eventId: string, input: AudienceInput) => string | null;
  /** Registra no histórico do evento que o WhatsApp foi aberto para cobrar um pendente. */
  whatsapp: (eventId: string, memberId: string) => void;
  /** "Datas em que não posso": marca ou desmarca a data para o próprio membro. */
  toggleUnavailableDate: (date: string) => void;
  markRead: (ids: string[]) => void;
  /**
   * Acompanha no estado a troca de perfil feita em Acessos (`accounts.changeRole`, que já
   * gravou): o selo do perfil e as permissões olham o `admin` do membro.
   */
  setMemberAdmin: (memberId: string, admin: boolean) => void;
  hideOnboarding: () => void;
  /**
   * Carrega a demonstração na igreja vazia (Primeiros passos, Configurações › Dados da
   * igreja). Com `replace`, limpa a igreja antes (o diálogo já pediu a confirmação).
   * Devolve se carregou.
   */
  loadDemoData: (options?: { replace?: boolean }) => Promise<boolean>;
  /** Apaga os dados da igreja; ficam os administradores, as configurações e os links. */
  clearChurchData: () => Promise<void>;
  /** Gera a proposta de escala (no navegador, ou na fila do servidor no modo API). */
  generate: (eventIds: string[], options: GenerationOptions) => Promise<GenerationResult>;
  /** Sai da sessão. */
  logout: () => void;
}

export interface StoreValue {
  state: DomainState;
  session: Session;
  /** A organização aberta. */
  org: Organization;
  /** Organizações: a página Organizações (plataforma) e o card de Configurações. */
  orgs: OrgsClient;
  /** O perfil de quem usa (com a API, o do login). */
  persona: Persona;
  /**
   * Quem usa não é membro da organização aberta (a plataforma dentro de outra igreja):
   * sem "Meu perfil", agenda nem confirmação de presença.
   */
  guest: boolean;
  permissions: Permissions;
  lookup: Lookup;
  today: string;
  me: Member;
  canUndo: boolean;
  toast: Toast | null;
  actions: StoreActions;
  /** E-mail do acesso (na store em memória, o do cadastro ou um fictício derivado do nome). */
  email: string;
  /** Acessos dos membros. */
  accounts: AccountsClient;
  /** Link da agenda pessoal. */
  calendar: CalendarClient;
  /** Privacidade da conta: aceite dos documentos, comunicações e a cópia dos dados. */
  privacy: PrivacyClient;
}

const StoreContext = createContext<StoreValue | null>(null);

type Props = {
  children: ReactNode;
  /** Estado inicial: o do servidor (`SessionGate`) ou o dos testes; sem ele, os dados de exemplo. */
  initialState?: DomainState;
  /** O perfil na store em memória (testes); com a API, vale o do login. */
  initialRole?: Persona;
  /** A sessão do login, as versões dos eventos e o cliente da API. */
  api?: ApiConnection;
  /** Sem a API (só nos testes): os clientes em memória (`test/memory`). */
  memory?: (source: MemorySource) => MemoryClients;
};

export interface ApiConnection {
  session: Session;
  email: string;
  /** Nome de quem entrou (para quando ele não é membro da organização aberta). */
  name: string;
  /** A organização ativa. */
  org: Organization;
  /** O aceite dos documentos e as comunicações, vindos de `GET /auth/me`. */
  privacy?: AccountPrivacy;
  /** Abre outra organização (plataforma): recarrega a sessão nela. */
  switchOrg: (orgId: string) => void;
  versions: Map<string, number>;
  client: Api;
  /** Recarrega o estado inteiro do servidor (depois de um erro de sincronização). */
  reload: () => Promise<{ state: DomainState; versions: Map<string, number> }>;
  logout: () => Promise<void>;
}

let idCounter = 0;
/** Id único na sessão — `Date.now()` sozinho repete quando duas ações caem no mesmo milissegundo. */
const nextId = () => `${Date.now().toString(36)}${(idCounter++).toString(36)}`;

export function AppStoreProvider({ children, initialState, initialRole, api, memory }: Props) {
  const [today] = useState(todayISO);
  const [state, setState] = useState<DomainState>(() => initialState ?? createSampleState(today));
  const [role, setRoleState] = useState<Persona>(() => {
    if (api) return api.session.platformAdmin ? 'platform' : api.session.role;

    return initialRole ?? 'platform';
  });
  // Store em memória: as organizações e o estado das que não estão abertas.
  const [world, setWorld] = useState<MemoryWorld>(SAMPLE_WORLD);
  const [apiOrg, setApiOrg] = useState<Organization | null>(api?.org ?? null);
  const worldRef = useRef(world);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Leitura do estado atual dentro das ações, sem recriá-las a cada render.
  const stateRef = useRef(state);
  const roleRef = useRef(role);
  const undoRef = useRef(undoStack);

  useEffect(() => {
    stateRef.current = state;
    roleRef.current = role;
    undoRef.current = undoStack;
    worldRef.current = world;
  });

  // A ponte com a API (modo API), criada uma vez; o aviso e a recarga são ligados abaixo.
  const [remote] = useState<Remote | null>(() => (api ? createRemote(api.client, api.versions) : null));

  useEffect(() => () => clearTimeout(timer.current), []);

  const sessionMemberId = api?.session.memberId;
  const guestName = api?.name;
  const guestEmail = api?.email;
  const meOf = useCallback(
    (s: DomainState, r: Persona, w: MemoryWorld): Member => {
      const id =
        sessionMemberId ?? (r === 'admin' ? (w.admins[w.orgId] ?? DEMO_ORG_ADMIN_ID) : DEMO_MEMBER_OF[r]);
      const found = s.members.find((m) => m.id === id);

      if (found) return found;

      // A plataforma em outra organização: o cadastro dela fica na organização de exemplo.
      const home = w.stash[SAMPLE_ORG.id]?.members.find((m) => m.id === id);

      return guestMember(id, guestName ?? home?.name ?? 'Marcos Andrade', guestEmail ?? home?.email ?? '');
    },
    [sessionMemberId, guestName, guestEmail]
  );

  // Criados uma vez: a store em memória é lida pela referência. Os getters só rodam nas
  // chamadas (handlers e consultas), nunca no render — o lint não enxerga isso.
  // eslint-disable-next-line react-hooks/refs
  const [clients] = useState<MemoryClients>(() => {
    if (api) {
      return {
        accounts: apiAccounts(api.client),
        privacy: apiPrivacy(api.client, api.privacy),
        orgs: apiOrgs(api.client),
        calendar: apiCalendar(api.client),
      };
    }
    if (!memory) throw new Error('AppStoreProvider precisa da API ou, nos testes, dos clientes em memória.');

    return memory({
      members: () => stateRef.current.members,
      ministries: () => stateRef.current.ministries,
      me: () => meOf(stateRef.current, roleRef.current, worldRef.current),
      state: () => stateRef.current,
      org: () => worldRef.current.orgs.find((o) => o.id === worldRef.current.orgId) ?? SAMPLE_ORG,
      orgs: () => worldRef.current.orgs,
      stateOf: (orgId) =>
        orgId === worldRef.current.orgId ? stateRef.current : worldRef.current.stash[orgId],
      adminOf: (orgId) => {
        if (orgId === SAMPLE_ORG.id) return { memberId: DEMO_ADMIN_ID, status: 'active' };

        const memberId = worldRef.current.admins[orgId];

        return memberId ? { memberId, status: 'invited' } : null;
      },
      addOrg: (input) => {
        const seq = nextId();
        const org: Organization = {
          id: `org${seq}`,
          name: input.name.trim(),
          slug: input.slug,
          city: (input.address?.city || input.city).trim(),
          uf: input.address?.uf || input.uf,
          address: input.address ?? { ...EMPTY_ADDRESS },
          location: null,
          color: input.color,
          status: 'active',
          createdAt: today,
        };
        const admin = { id: `m${seq}`, name: input.admin.name, email: input.admin.email };
        const created = createOrgState(admin, { seed: seq, suggested: input.suggested });
        const next: MemoryWorld = {
          ...worldRef.current,
          orgs: [...worldRef.current.orgs, org],
          stash: { ...worldRef.current.stash, [org.id]: created },
          admins: { ...worldRef.current.admins, [org.id]: admin.id },
        };

        worldRef.current = next;
        setWorld(next);

        return org;
      },
      putOrg: (org) => {
        const next = {
          ...worldRef.current,
          orgs: worldRef.current.orgs.map((o) => (o.id === org.id ? org : o)),
        };

        worldRef.current = next;
        setWorld(next);
      },
    });
  });
  const { accounts, privacy, orgs, calendar } = clients;

  const actions = useMemo<StoreActions>(() => {
    const who = () => meOf(stateRef.current, roleRef.current, worldRef.current).name;
    const memberName = (id: string) => stateRef.current.members.find((m) => m.id === id)?.name ?? '';
    /** A foto do membro como o servidor gravou (a da solicitação muda de endereço ao passar). */
    const keepPhoto = (memberId: string, photo: string | null | undefined) => {
      if (photo === undefined) return;
      setState((s) => ({ ...s, members: s.members.map((m) => (m.id === memberId ? { ...m, photo } : m)) }));
    };
    const swapById = (eventId: string, swapId: string) =>
      stateRef.current.events.find((e) => e.id === eventId)?.swaps.find((w) => w.id === swapId);
    /** A função em que a pessoa está no evento (a do pedido de troca). */
    const swapRoleName = (eventId: string, memberId: string) => {
      const event = stateRef.current.events.find((e) => e.id === eventId);
      const roleId = event && Object.entries(event.slots).find(([, ids]) => ids.includes(memberId))?.[0];

      return stateRef.current.ministries.flatMap((m) => m.roles).find((r) => r.id === roleId)?.name ?? '';
    };
    const roleName = (id: string) => {
      for (const ministry of stateRef.current.ministries) {
        const role = ministry.roles.find((r) => r.id === id);
        if (role) return role.name;
      }

      return '';
    };

    /** Funções do evento que o perfil edita (`null` = todas, o Administrador). */
    const editableRoles = (eventId: string): string[] | null => {
      const s = stateRef.current;
      const permissions = permissionsOf(
        sessionOf(roleRef.current, meOf(s, roleRef.current, worldRef.current).id),
        s.ministries
      );

      if (permissions.isAdmin) return null;

      const event = s.events.find((e) => e.id === eventId);

      return (event?.ministryIds ?? []).flatMap((ministryId) =>
        (s.ministries.find((m) => m.id === ministryId)?.roles ?? [])
          .filter((r) => permissions.editRole(r.id))
          .map((r) => r.id)
      );
    };

    /** As funções do evento que o perfil edita, sempre como lista (o Administrador, todas). */
    const historyRoles = (eventId: string): string[] => {
      const s = stateRef.current;
      const event = s.events.find((e) => e.id === eventId);
      const all = (event?.ministryIds ?? []).flatMap(
        (ministryId) => s.ministries.find((m) => m.id === ministryId)?.roles.map((r) => r.id) ?? []
      );
      const editable = editableRoles(eventId);

      return editable ? all.filter((id) => editable.includes(id)) : all;
    };

    /** Troca um evento do estado pelo resultado de `change` (as edições do Histórico). */
    const changeEvent = (eventId: string, change: (event: ScheduleEvent) => ScheduleEvent) =>
      setState((s) => ({ ...s, events: s.events.map((e) => (e.id === eventId ? change(e) : e)) }));

    /** Registro no histórico do evento, sem entrar no desfazer. */
    const logOnly = (eventId: string, text: string): LogEntry => ({
      id: `l${nextId()}`,
      eventId,
      who: who(),
      text,
      at: Date.now(),
    });

    const showToast = (message: string, options?: { undo?: Toast['undo']; tone?: ToastTone }) => {
      const id = Date.now();
      setToast({ id, message, undo: options?.undo ?? false, tone: options?.tone ?? 'info' });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setToast((t) => (t?.id === id ? null : t)), TOAST_MS);
    };

    /**
     * Confirma um cadastro (Ministérios e funções): em memória, na hora; com a API,
     * depois que o servidor gravou — se recusar, o aviso de erro (`onError`) já diz o motivo.
     */
    const confirmed = (message: string, synced: Promise<boolean> | undefined) => {
      if (!synced) {
        showToast(message, { tone: 'success' });

        return;
      }

      void synced.then((ok) => {
        if (ok) showToast(message, { tone: 'success' });
      });
    };

    /** Mudança na escala: guarda o "antes" para desfazer, registra no histórico e avisa. */
    const commit = (
      change: (events: ScheduleEvent[]) => ScheduleEvent[],
      message: string,
      eventIds: string[],
      logText = message
    ) => {
      const before = stateRef.current.events;
      const next = change(before);
      const at = Date.now();
      const entries: LogEntry[] = eventIds.map((eventId) => ({
        id: `l${nextId()}`,
        eventId,
        who: who(),
        text: logText,
        at,
      }));

      setUndoStack((stack) => [
        ...stack.slice(-(UNDO_LIMIT - 1)),
        { events: before, after: touchedEvents(before, next), eventIds, message: logText },
      ]);
      setState((s) => ({
        ...s,
        events: s.events === before ? next : change(s.events),
        log: [...entries, ...s.log],
      }));
      showToast(message, { undo: true });
    };

    /** O "Desfazer" do aviso da resposta: volta à resposta de antes (e à função, na recusa). */
    const undoResponse = (eventId: string, memberId: string, previous: ScheduleResponse | null) => {
      const event = stateRef.current.events.find((e) => e.id === eventId);

      if (!event || !canRetract(event, memberId, today)) {
        showToast('Não dá mais para desfazer: a vaga já foi preenchida ou a escala mudou.');

        return;
      }

      const declined = event.responses[memberId]?.status === 'declined';

      setState((s) => ({
        ...s,
        events: T.retractResponse(s.events, eventId, memberId, previous),
        log: [
          logOnly(
            eventId,
            declined
              ? `${who()} desfez "não posso ir" e voltou à escala`
              : `${who()} desfez a confirmação de presença`
          ),
          ...s.log,
        ],
      }));
      remote?.undoResponse(eventId, previous ? 'confirmed' : 'pending');
      showToast(declined ? 'Pronto: você continua na escala.' : 'Confirmação desfeita.');
    };

    const store: StoreActions = {
      switchOrg: (orgId) => {
        if (api) {
          void remote?.idle().then(() => api.switchOrg(orgId));

          return;
        }

        const w = worldRef.current;
        const target = w.stash[orgId];
        const org = w.orgs.find((o) => o.id === orgId);

        if (!target || !org || orgId === w.orgId) return;

        const { [orgId]: _opened, ...rest } = w.stash;
        const next: MemoryWorld = { ...w, orgId, stash: { ...rest, [w.orgId]: stateRef.current } };

        worldRef.current = next;
        setWorld(next);
        setState(target);
        setUndoStack([]);
        // O perfil que não existe na organização volta a ser o da plataforma.
        if (!personasOf(next).includes(roleRef.current)) setRoleState('platform');
        showToast(`Você está em ${org.name}`);
      },
      updateOrg: async (patch) => {
        const current = api ? apiOrg : worldRef.current.orgs.find((o) => o.id === worldRef.current.orgId);

        if (!current) return;

        const saved = await orgs.update(current.id, patch);

        if (api) setApiOrg(saved);
        showToast('Dados da organização atualizados');
      },
      approveRegistration: (registrationId) => {
        const s = stateRef.current;
        const registration = s.registrations.find((r) => r.id === registrationId);

        if (!registration) return 'Solicitação não encontrada.';

        const error = approvalError(registration, s.members);

        if (error) return error;

        const memberId = `m${nextId()}`;

        setState((st) => ({
          ...st,
          ...T.approveRegistration(st, registrationId, memberId, who(), Date.now()),
        }));
        void remote
          ?.approveRegistration(registrationId, memberId)
          .then((photo) => keepPhoto(memberId, photo));
        showToast(`${registration.data.name} incluído(a) nos membros. Envie o convite de acesso em Acessos.`);

        return null;
      },
      mergeRegistration: (registrationId, memberId, fields) => {
        const s = stateRef.current;
        const registration = s.registrations.find((r) => r.id === registrationId);

        if (!registration) return 'Solicitação não encontrada.';

        const error = mergeError(registration, memberId, fields, s.members, today);

        if (error) return error;

        setState((st) => ({
          ...st,
          ...T.mergeRegistration(st, registrationId, memberId, fields, who(), Date.now()),
        }));
        void remote
          ?.mergeRegistration(registrationId, memberId, fields)
          .then((photo) => keepPhoto(memberId, photo));

        const name = s.members.find((m) => m.id === memberId)?.name ?? '';

        showToast(`Solicitação vinculada ao cadastro de ${name}.`);

        return null;
      },
      rejectRegistration: (registrationId, reason) => {
        const name = stateRef.current.registrations.find((r) => r.id === registrationId)?.data.name ?? '';

        setState((st) => ({
          ...st,
          registrations: T.rejectRegistration(st.registrations, registrationId, reason, who(), Date.now()),
        }));
        remote?.rejectRegistration(registrationId, reason);
        showToast(`Solicitação de ${name} recusada`);
      },
      rotateRegistrationLink: async () => {
        const link = remote
          ? await remote.rotateRegistrationLink()
          : T.rotateRegistrationLink(Math.random().toString(36).slice(2, 10), Date.now());

        if (!link) return;

        setState((st) => ({ ...st, registrationLink: link }));
        showToast('Novo link gerado. O anterior parou de funcionar.');
      },
      setRegistrationLinkActive: (active) => {
        const link = stateRef.current.registrationLink;

        if (link) setState((st) => ({ ...st, registrationLink: T.setRegistrationLinkActive(link, active) }));
        remote?.setRegistrationLinkActive(active);
        showToast(
          active ? 'Cadastros retomados' : 'Cadastros pausados: quem abrir o link verá "Cadastros encerrados"'
        );
      },
      saveVisitor: (draft, id) => {
        const s = stateRef.current;
        const first = Object.values(V.visitorErrors(id ? { ...draft, consent: true } : draft, today))[0];

        if (first) return { error: first };

        const input = { ...draft, consent: true as const };

        if (id) {
          setState((st) => ({ ...st, visitors: V.updateVisitor(st.visitors, id, draft) }));
          remote?.updateVisitor(id, input);
          showToast('Ficha salva');

          return { id };
        }

        const newId = `vis${nextId()}`;

        if (V.visitorMatch(s.visitors, draft)) {
          return { error: 'Já existe uma ficha com este celular ou e-mail. Registre a visita nela.' };
        }

        setState((st) => ({
          ...st,
          visitors: V.addVisitor(st.visitors, newId, draft, { who: who(), at: Date.now() }),
        }));
        remote?.createVisitor({ ...input, id: newId });
        showToast(`Ficha de ${draft.name.trim()} registrada`, { tone: 'success' });

        return { id: newId };
      },
      recordVisitorVisit: (visitorId, visit) => {
        setState((st) => ({ ...st, visitors: V.recordVisit(st.visitors, visitorId, visit) }));
        remote?.recordVisitorVisit(visitorId, visit);
        showToast('Visita registrada');
      },
      moveVisitor: (visitorId, stage) => {
        setState((st) => ({ ...st, visitors: V.moveVisitor(st.visitors, visitorId, stage, Date.now()) }));
        remote?.moveVisitor(visitorId, stage);
        showToast(`Movida para ${V.stageLabel(stateRef.current.settings, stage)}`);
      },
      assignVisitorOwner: (visitorId, ownerId) => {
        setState((st) => ({ ...st, visitors: V.assignOwner(st.visitors, visitorId, ownerId) }));
        remote?.assignVisitorOwner(visitorId, ownerId);
        showToast(ownerId ? `Responsável: ${memberName(ownerId)}` : 'Ficha sem responsável');
      },
      addVisitorFollowUp: (visitorId, draft) => {
        const error = V.followUpError(draft);

        if (error) return error;

        const id = `vfu${nextId()}`;

        setState((st) => ({ ...st, visitors: V.addFollowUp(st.visitors, visitorId, id, draft) }));
        remote?.addVisitorFollowUp(visitorId, { id, ...draft });
        showToast('Acompanhamento criado');

        return null;
      },
      updateVisitorFollowUp: (visitorId, followUpId, patch) => {
        setState((st) => ({
          ...st,
          visitors: V.updateFollowUp(st.visitors, visitorId, followUpId, patch, {
            who: who(),
            at: Date.now(),
          }),
        }));
        remote?.updateVisitorFollowUp(visitorId, followUpId, patch);
        if (patch.status === 'done') showToast('Acompanhamento concluído', { tone: 'success' });
      },
      logVisitorContact: (visitorId, kind, outcome) => {
        const id = `vct${nextId()}`;

        setState((st) => ({
          ...st,
          visitors: V.logContact(
            st.visitors,
            visitorId,
            { id, kind, outcome },
            { who: who(), at: Date.now(), journey: st.settings.visitorJourney }
          ),
        }));
        remote?.logVisitorContact(visitorId, { id, kind, outcome });
        showToast(
          outcome === 'declined' ? 'Contato registrado. A ficha foi arquivada.' : 'Contato registrado'
        );
      },
      referVisitor: (visitorId) => {
        const visitor = stateRef.current.visitors.find((v) => v.id === visitorId);

        if (!visitor) return 'Ficha não encontrada.';

        const error = V.referralError(visitor);

        if (error) return error;

        const registrationId = `reg${nextId()}`;

        setState((st) => ({ ...st, ...V.referToRegistration(st, visitorId, registrationId, Date.now()) }));
        remote?.referVisitor(visitorId, registrationId);
        showToast(`Cadastro de ${visitor.name} enviado para aprovação em Membros`, { tone: 'success' });

        return null;
      },
      linkVisitorRegistration: (visitorId, registrationId) => {
        setState((st) => ({ ...st, visitors: V.linkRegistration(st.visitors, visitorId, registrationId) }));
        remote?.linkVisitorRegistration(visitorId, registrationId);
        showToast('Ficha ligada ao cadastro');
      },
      anonymizeVisitor: (visitorId) => {
        setState((st) => ({ ...st, visitors: V.anonymizeVisitor(st.visitors, visitorId, Date.now()) }));
        remote?.anonymizeVisitor(visitorId);
        showToast('Ficha anonimizada');
      },
      rotateVisitorLink: async () => {
        const link = remote
          ? await remote.rotateVisitorLink()
          : V.rotateVisitorLink(Math.random().toString(36).slice(2, 10), Date.now());

        if (!link) return;

        setState((st) => ({ ...st, visitorLink: link }));
        showToast('Novo link gerado. O anterior (e o QR impresso) parou de funcionar.');
      },
      setVisitorLinkActive: (active) => {
        const link = stateRef.current.visitorLink;

        if (link) setState((st) => ({ ...st, visitorLink: { ...link, active } }));
        remote?.setVisitorLinkActive(active);
        showToast(active ? 'Link da ficha reaberto' : 'Link da ficha pausado');
      },
      submitPrivacyRequest: (kind, note) => {
        const s = stateRef.current;
        const me = meOf(s, roleRef.current, worldRef.current);

        if (!s.members.some((m) => m.id === me.id)) return 'Você não tem cadastro nesta organização.';

        const error = privacyRequestError(s.privacyRequests, me.id, kind, note);

        if (error) return error;

        const id = `priv${nextId()}`;

        setState((st) => ({
          ...st,
          privacyRequests: submitPrivacyRequest(st.privacyRequests, {
            id,
            memberId: me.id,
            kind,
            note,
            at: Date.now(),
            today,
          }),
        }));
        remote?.submitPrivacyRequest({ id, kind, note });
        showToast('Pedido enviado. A igreja responde em até 15 dias.', { tone: 'success' });

        return null;
      },
      resolvePrivacyRequest: (requestId, resolution) => {
        const s = stateRef.current;
        const request = s.privacyRequests.find((r) => r.id === requestId);
        const me = meOf(s, roleRef.current, worldRef.current);
        // Em memória, os administradores são os de exemplo; com a API, o servidor confere.
        const adminIds = api
          ? []
          : [DEMO_ADMIN_ID, DEMO_ORG_ADMIN_ID, ...Object.values(worldRef.current.admins)].filter((id) =>
              s.members.some((m) => m.id === id)
            );
        const error = resolutionError(request, resolution, { actorId: me.id, adminIds });

        if (error || !request) return error ?? 'Pedido não encontrado.';

        const name = s.members.find((m) => m.id === request.memberId)?.name ?? '';

        setState((st) => ({
          ...st,
          ...resolvePrivacyRequest(st, requestId, resolution, { who: who(), at: Date.now(), today }),
        }));
        // A anonimização não se desfaz: o "antes" das escalas traria a pessoa de volta.
        if (resolution.anonymize) setUndoStack([]);
        remote?.resolvePrivacyRequest(requestId, resolution);
        showToast(
          resolution.anonymize
            ? `Cadastro de ${name} anonimizado e pedido atendido.`
            : `Pedido de ${name} ${resolution.status === 'done' ? 'atendido' : 'recusado'}.`,
          { tone: 'success' }
        );

        return null;
      },
      showToast,
      hideToast: () => setToast(null),
      undo: () => {
        const last = undoRef.current[undoRef.current.length - 1];

        if (!last) return;

        const current = stateRef.current.events;
        const restored = undoEvents(current, last);

        setUndoStack((stack) => stack.slice(0, -1));

        if (!restored) {
          showToast('Não dá mais para desfazer: a escala mudou depois dessa alteração.');

          return;
        }

        const at = Date.now();

        remote?.restore(current, restored, last.message);
        setState((s) => ({
          ...s,
          events: restored,
          log: [
            ...last.eventIds.map((eventId) => ({
              id: `u${nextId()}`,
              eventId,
              who: who(),
              text: `Desfez: ${last.message}`,
              at,
            })),
            ...s.log,
          ],
        }));
        showToast('Alteração desfeita');
      },
      assign: (eventId, roleId, memberId, replaceId) => {
        const message = replaceId
          ? `${memberName(memberId)} substituiu ${memberName(replaceId)} em ${roleName(roleId)}`
          : `${memberName(memberId)} escalado(a) em ${roleName(roleId)}`;

        commit((events) => T.assignMember(events, eventId, roleId, memberId, replaceId), message, [eventId]);
        remote?.assign(eventId, roleId, memberId, replaceId);
      },
      remove: (eventId, roleId, memberId) => {
        commit(
          (events) => T.removeMember(events, eventId, roleId, memberId),
          `${memberName(memberId)} removido(a) de ${roleName(roleId)}`,
          [eventId]
        );
        remote?.remove(eventId, roleId, memberId);
      },
      move: (eventId, fromRoleId, toRoleId, memberId) => {
        commit(
          (events) => T.moveMember(events, eventId, fromRoleId, toRoleId, memberId),
          `${memberName(memberId)} movido(a) para ${roleName(toRoleId)}`,
          [eventId]
        );
        remote?.move(eventId, fromRoleId, toRoleId, memberId);
      },
      publish: (eventId) => {
        commit((events) => T.publishEvent(events, eventId), 'Escala publicada', [eventId]);
        remote?.publish(eventId);
      },
      applyGeneration: (result, publish, adjustments = [], adjusted = result) => {
        const n = result.eventIds.length;

        commit(
          (events) => T.applyGeneration(events, adjusted, publish),
          publish
            ? `Escala publicada · ${n} ${n > 1 ? 'eventos' : 'evento'}`
            : `Rascunho salvo · ${n} ${n > 1 ? 'eventos' : 'evento'}`,
          result.eventIds,
          publish ? 'Escala gerada automaticamente e publicada' : 'Escala gerada automaticamente (rascunho)'
        );
        remote?.applyGeneration(result, publish, adjustments);
      },
      createEvents: (draft) => {
        const seed = nextId();
        const { events, created } = T.createEvents(
          stateRef.current.events,
          draft,
          stateRef.current.ministries,
          seed
        );
        const title = draft.title.trim();

        commit(
          () => events,
          created.length > 1 ? `${created.length} eventos criados · ${title}` : `Evento criado · ${title}`,
          [],
          ''
        );
        remote?.createEvents(draft, seed);

        return created[0]?.seriesId ?? '';
      },
      backfill: (seriesId, since) => {
        const { events, created } = backfillEvents(stateRef.current.events, seriesId, since);

        if (!created.length) return 0;

        const n = created.length;

        commit(
          () => events,
          `${n} ${n === 1 ? 'ocorrência passada criada' : 'ocorrências passadas criadas'} · ${created[0]!.title}`,
          [],
          ''
        );
        remote?.backfill(seriesId, since);

        return n;
      },
      addOccurrence: (seriesId, date, time) => {
        const error = occurrenceError(stateRef.current.events, seriesId, date);

        if (error) return error;

        const { events, created } = addOccurrence(stateRef.current.events, seriesId, date, time);

        commit(() => events, `Nova data criada · ${created!.title}, ${formatDayMonth(date)}`, [], '');
        remote?.addOccurrence(seriesId, date, time);

        return null;
      },
      restoreSkipped: (seriesId, date) => {
        const error = restoreSkippedError(stateRef.current.events, seriesId, date);

        if (error) return error;

        const { events, created } = restoreSkipped(stateRef.current.events, seriesId, date);

        commit(() => events, `Data restaurada · ${created!.title}, ${formatDayMonth(date)}`, [], '');
        remote?.restoreSkipped(seriesId, date);

        return null;
      },
      setServed: (eventId, roleId, memberId, served) => {
        changeEvent(eventId, (e) => setServed(e, roleId, memberId, served));
        remote?.served(eventId, roleId, memberId, served);
      },
      copyServedFromPrevious: (eventId) => {
        const s = stateRef.current;
        const event = s.events.find((e) => e.id === eventId);
        const roleIds = historyRoles(eventId);
        const next = event ? copyServedFromPrevious(s.events, event, roleIds) : null;

        if (!next) return false;

        commit(
          (events) => events.map((e) => (e.id === eventId ? next : e)),
          'Copiado do evento anterior. Ajuste o que mudou.',
          [eventId],
          'Histórico copiado do evento anterior'
        );
        remote?.copyServed(eventId, roleIds);

        return true;
      },
      clearServed: (eventId) => {
        const roleIds = historyRoles(eventId);

        commit(
          (events) => events.map((e) => (e.id === eventId ? clearServed(e, roleIds) : e)),
          'Marcações limpas',
          [eventId],
          'Marcações do histórico limpas'
        );
        remote?.clearServed(eventId, roleIds);
      },
      setRecorded: (eventId, recorded) => {
        changeEvent(eventId, (e) => setRecorded(e, recorded));
        setState((s) => ({
          ...s,
          log: [logOnly(eventId, recorded ? 'Histórico registrado' : 'Histórico reaberto'), ...s.log],
        }));
        remote?.setRecorded(eventId, recorded);
      },
      updateEvents: (draft, choices = { includeScheduled: true, includePastEmpty: true }) => {
        const one = draft.scope === 'one' || !draft.isRecurring;
        const options = { today, ...choices };
        const impact = editImpact(stateRef.current.events, draft, stateRef.current.ministries, options);
        const ids = [...impact.applied, ...impact.titleOnly];
        // O término da série, se mudou: as datas novas e as que saem entram no aviso.
        const end =
          !one && draft.until && draft.seriesId
            ? seriesEndPlan(stateRef.current.events, draft.seriesId, draft.until, today)
            : null;
        const ended = end && !end.error && (end.add.length || end.remove.length);
        const endText = [
          end?.add.length ? `${end.add.length} ${end.add.length === 1 ? 'data nova' : 'datas novas'}` : '',
          end?.remove.length
            ? `${end.remove.length} ${end.remove.length === 1 ? 'removida' : 'removidas'}`
            : '',
        ]
          .filter(Boolean)
          .map((t) => ` · ${t}`)
          .join('');

        commit(
          (events) => T.updateEvents(events, draft, stateRef.current.ministries, options),
          one
            ? 'Evento atualizado'
            : `Série atualizada · ${ids.length} ${ids.length === 1 ? 'data' : 'datas'}${ended ? endText : ''}`,
          ids,
          {
            one: 'Evento editado',
            following: `Série editada a partir de ${formatDayMonth(draft.date)}`,
            series: 'Série editada',
          }[one ? 'one' : draft.scope] + (ended ? ` · término em ${formatDayMonth(draft.until!)}` : '')
        );
        remote?.updateEvents(draft, choices);
      },
      deleteEvent: (event, scope, reason = '') => {
        if (scope === 'one' && skipsDate(stateRef.current.events, event)) {
          const notified = cancellationAudience(event, today).length;
          const told = notified
            ? ` · ${notified} ${notified === 1 ? 'pessoa avisada' : 'pessoas avisadas'}`
            : '';

          commit(
            (events) => skipOccurrence(events, event, { reason, today, at: Date.now() }),
            `Data excluída · ${event.title}, ${formatDayMonth(event.date)}${told}`,
            []
          );
          remote?.skipOccurrence(event, reason);

          return;
        }

        const n = T.eventsInScope(stateRef.current.events, event, scope).length;
        const message = {
          one: `Evento excluído · ${event.title}, ${formatDayMonth(event.date)}`,
          following: `Série encerrada em ${formatDayMonth(event.date)} · ${n} ${n === 1 ? 'evento' : 'eventos'}`,
          series: `Série excluída · ${n} eventos`,
        }[scope];

        commit((events) => T.deleteEvents(events, event, scope), message, []);
        remote?.deleteEvent(event, scope);
      },
      saveMinistry: (draft) => {
        const error = T.validateMinistry(stateRef.current.ministries, draft);

        if (error) return { ok: false, error };

        const id = draft.id ?? `min${nextId()}`;
        setState((s) => ({ ...s, ministries: T.saveMinistry(s.ministries, draft, id) }));
        confirmed(
          draft.id
            ? `Ministério ${draft.name.trim()} atualizado`
            : `Ministério ${draft.name.trim()} criado. Adicione as funções.`,
          remote?.saveMinistry(draft, id)
        );

        return { ok: true, id };
      },
      createFromTemplate: (draft) => {
        const error = templateDraftError(stateRef.current.ministries, draft);

        if (error) return { ok: false, error };

        const id = `min${nextId()}`;
        const roles = roleSeeds(draft).map((seed) => roleFromSeed(seed, `f${nextId()}`));
        const name = draft.ministry.name.trim();

        setState((s) => ({ ...s, ministries: T.saveMinistry(s.ministries, draft.ministry, id, roles) }));
        confirmed(
          `Ministério ${name} criado com ${roles.length} ${roles.length === 1 ? 'função' : 'funções'}`,
          remote?.saveMinistry(draft.ministry, id, roles)
        );

        return { ok: true, id };
      },
      deleteMinistry: (ministryId) => {
        const name = stateRef.current.ministries.find((m) => m.id === ministryId)?.name ?? '';

        setState((s) => T.deleteMinistry(s, ministryId));
        // Os "antes" guardados ainda citam o ministério: desfazer o traria de volta pela metade.
        setUndoStack([]);
        confirmed(`Ministério ${name} excluído`, remote?.deleteMinistry(ministryId));
      },
      addRole: (ministryId) => {
        const id = `f${nextId()}`;
        const name = stateRef.current.ministries.find((m) => m.id === ministryId)?.name ?? '';

        setState((s) => ({ ...s, ministries: T.addRole(s.ministries, ministryId, id) }));
        confirmed(`Função criada em ${name}. Dê um nome a ela.`, remote?.addRole(ministryId, id));

        return id;
      },
      updateRole: (ministryId, roleId, patch) => {
        const role = stateRef.current.ministries
          .find((m) => m.id === ministryId)
          ?.roles.find((r) => r.id === roleId);

        if (!role) return;

        setState((s) => ({ ...s, ministries: T.updateRole(s.ministries, ministryId, roleId, patch) }));
        confirmed(
          describeRoleChange(role, patch, (id) => memberName(id) || 'Membro'),
          remote?.updateRole(roleId, patch)
        );
      },
      deleteRole: (ministryId, roleId) => {
        const name = roleName(roleId);

        setState((s) => T.deleteRole(s, ministryId, roleId));
        setUndoStack([]);
        confirmed(`Função ${name} excluída`, remote?.deleteRole(roleId));
      },
      enableInRole: (memberId, ministryId, roleId) => {
        const s = stateRef.current;
        const role = s.ministries.find((m) => m.id === ministryId)?.roles.find((r) => r.id === roleId);

        if (!role) return;

        setState((st) => ({ ...st, members: T.enableInRole(st.members, memberId, ministryId, roleId) }));
        confirmed(
          `${memberName(memberId)} habilitado(a) em ${role.name}`,
          remote?.enableInRole(roleId, memberId)
        );
      },
      disableInRole: (memberId, ministryId, roleId) => {
        const s = stateRef.current;
        const role = s.ministries.find((m) => m.id === ministryId)?.roles.find((r) => r.id === roleId);
        const member = s.members.find((m) => m.id === memberId);

        if (!role || !member) return;

        const impact = T.roleRemovalImpact(s, memberId, roleId, today);

        setState((st) => ({
          ...st,
          ...T.disableInRole(st, memberId, ministryId, roleId, today),
          log: [
            ...impact.events.map((event) =>
              logOnly(event.id, `${member.name} saiu de ${role.name}: tirado(a) da função`)
            ),
            ...st.log,
          ],
        }));
        setUndoStack([]);
        confirmed(`${member.name} tirado(a) de ${role.name}`, remote?.disableInRole(roleId, memberId));
      },
      newMemberDraft: () => T.newMember(`m${nextId()}`, { name: '', birthDate: null, sex: 'F' }),
      saveMember: (draft) => {
        const current = stateRef.current.members.find((m) => m.id === draft.id);
        const isNew = !current;
        // A situação nas escalas e a foto não vêm do formulário: mudam só pelas ações delas.
        const member = {
          ...draft,
          inactiveSince: current?.inactiveSince ?? null,
          photo: current?.photo ?? null,
        };

        setState((s) => {
          const members = T.saveMember(s.members, member);

          return { ...s, members, ministries: T.releaseRoleLeaders(s.ministries, members) };
        });
        remote?.saveMember(member, isNew);
        showToast(isNew ? `${member.name} cadastrado(a)` : 'Alterações salvas');
      },
      setMemberPhoto: async (memberId, upload) => {
        if (!stateRef.current.members.some((m) => m.id === memberId)) return false;

        const photo = remote
          ? await remote.setMemberPhoto(memberId, upload)
          : upload && photoDataUrl(upload.type, upload.data);

        if (photo === undefined) return false;

        setState((s) => ({ ...s, members: s.members.map((m) => (m.id === memberId ? { ...m, photo } : m)) }));
        showToast(photo ? 'Foto atualizada' : 'Foto removida', { tone: 'success' });

        return true;
      },
      setMemberInactive: (memberId, inactive) => {
        const s = stateRef.current;
        const member = s.members.find((m) => m.id === memberId);

        if (!member || inactive !== isActiveMember(member)) return null;

        if (inactive) {
          const error = deactivationError(memberId, meOf(s, roleRef.current, worldRef.current).id);

          if (error) return error;

          const impact = deactivationImpact(s, memberId, today);

          setState((st) => ({
            ...st,
            ...deactivateMember(st, memberId, today),
            log: [
              ...impact.events.map(({ event, roles }) =>
                logOnly(
                  event.id,
                  `${member.name} saiu de ${roles.map((r) => r.name).join(', ')}: membro inativado`
                )
              ),
              ...st.log,
            ],
          }));
          // O "antes" das escalas traria a pessoa de volta.
          setUndoStack([]);
        } else {
          setState((st) => ({ ...st, members: reactivateMember(st.members, memberId) }));
        }
        remote?.setMemberInactive(memberId, inactive);
        showToast(inactive ? `${member.name} inativado(a)` : `${member.name} reativado(a)`, {
          tone: 'success',
        });

        return null;
      },
      importSeed: () => nextId(),
      profileLink: async (memberId) => {
        const s = stateRef.current;
        const member = s.members.find((m) => m.id === memberId);
        const permissions = permissionsOf(
          sessionOf(roleRef.current, meOf(s, roleRef.current, worldRef.current).id),
          s.ministries
        );

        if (!member) return { error: 'O membro não existe mais.' };
        if (!canShareProfileLink(permissions, member))
          return { error: 'Você só gera o link de quem está nas funções que você escala.' };
        if (!isActiveMember(member))
          return { error: 'O cadastro está inativo: reative antes de mandar o link.' };

        if (!remote) {
          const org = worldRef.current.orgs.find((o) => o.id === worldRef.current.orgId) ?? SAMPLE_ORG;

          return {
            url: `https://${APP_HOST}${profileLinkPath(org.slug, `demo-${memberId}`)}`,
            expiresAt: new Date(Date.now() + PROFILE_LINK_DAYS * 86_400_000).toISOString(),
          };
        }

        try {
          const link = await remote.createProfileLink(memberId);

          return { url: `${window.location.origin}${link.path}`, expiresAt: link.expiresAt };
        } catch (error) {
          return { error: problemMessage(error) };
        }
      },
      importMembers: (plan) => {
        const s = stateRef.current;
        const permissions = permissionsOf(
          sessionOf(roleRef.current, meOf(s, roleRef.current, worldRef.current).id),
          s.ministries
        );

        if (!permissions.manageMembers) return 'Só o administrador importa membros.';

        const problem = plan.linked.length
          ? memberEditError(plan.linked, s.members, s.ministries, permissions)
          : null;

        if (problem) return problem.message;

        const created = plan.created.length;
        const linked = plan.linked.length;

        if (!created && !linked) return 'Nada para importar.';

        setState((st) => {
          const members = applyMemberEdits([...st.members, ...plan.created], plan.linked);

          return { ...st, members, ministries: T.releaseRoleLeaders(st.ministries, members) };
        });
        // Na fila, nesta ordem: os novos e, depois, o que muda nos que já estavam.
        if (created) remote?.importMembers(plan.created);
        if (linked) remote?.bulkEditMembers(plan.linked);

        const parts = [
          created ? `${created} ${created > 1 ? 'membros importados' : 'membro importado'}` : '',
          linked ? `${linked} já ${linked > 1 ? 'cadastrados atualizados' : 'cadastrado atualizado'}` : '',
        ].filter(Boolean);

        showToast(parts.join(' · '), { tone: 'success' });

        return null;
      },
      bulkEditMembers: (edits) => {
        const s = stateRef.current;

        if (!edits.length) return null;

        const permissions = permissionsOf(
          sessionOf(roleRef.current, meOf(s, roleRef.current, worldRef.current).id),
          s.ministries
        );
        const problem = memberEditError(edits, s.members, s.ministries, permissions);

        if (problem) return problem.message;

        const write = (list: readonly MemberEdit[], message: string, undo?: () => void) => {
          setState((st) => {
            const members = applyMemberEdits(st.members, list);

            return { ...st, members, ministries: T.releaseRoleLeaders(st.ministries, members) };
          });

          const synced = remote?.bulkEditMembers(list);
          const done = () => showToast(message, { tone: 'success', undo });

          if (synced) void synced.then((ok) => ok && done());
          else done();
        };
        const back = revertEdits(s.members, edits);

        write(edits, edits.length === 1 ? '1 membro atualizado' : `${edits.length} membros atualizados`, () =>
          write(back, 'Alterações desfeitas')
        );

        return null;
      },
      updateSettings: (patch) => {
        setState((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
        remote?.updateSettings(patch);
      },
      respond: (eventId, status, reason = '') => {
        const memberId = meOf(stateRef.current, roleRef.current, worldRef.current).id;
        const previous = stateRef.current.events.find((e) => e.id === eventId)?.responses[memberId] ?? null;
        const clean = reason.trim();
        const text =
          status === 'confirmed'
            ? `${who()} confirmou presença`
            : `${who()} não pode ir${clean ? ` (${clean})` : ''} — vaga reaberta`;

        setState((s) => ({
          ...s,
          events: T.respond(s.events, eventId, memberId, { status, reason: clean, at: Date.now() }),
          log: [logOnly(eventId, text), ...s.log],
        }));
        remote?.respond(eventId, status, clean);
        showToast(
          status === 'confirmed'
            ? 'Presença confirmada. Obrigado!'
            : 'Avisamos o líder que você não pode ir. A vaga foi reaberta.',
          { undo: () => undoResponse(eventId, memberId, previous) }
        );
      },
      swapCandidates: async (eventId) => {
        if (remote) return remote.swapCandidates(eventId);

        const state = stateRef.current;
        const memberId = meOf(state, roleRef.current, worldRef.current).id;
        const event = state.events.find((e) => e.id === eventId);
        const roleId = event && Object.entries(event.slots).find(([, ids]) => ids.includes(memberId))?.[0];

        if (!roleId) return [];

        return swapCandidates(state.events, eventId, roleId, memberId, buildLookup(state, today)).map(
          (c) => ({ memberId: c.member.id, state: c.state, blocked: c.blocked })
        );
      },
      requestSwap: (eventId, toId) => {
        const fromId = meOf(stateRef.current, roleRef.current, worldRef.current).id;
        const id = `sw${nextId()}`;
        const role = swapRoleName(eventId, fromId);

        setState((s) => ({
          ...s,
          events: T.requestSwap(s.events, eventId, { id, fromId, toId, at: Date.now() }),
          log: [logOnly(eventId, `Pediu troca com ${memberName(toId)} em ${role}`), ...s.log],
        }));
        remote?.requestSwap(eventId, id, toId);
        showToast(`Pedido de troca enviado para ${memberName(toId)}.`, { tone: 'success' });
      },
      cancelSwap: (eventId, swapId) => {
        setState((s) => ({
          ...s,
          events: T.cancelSwap(s.events, eventId, swapId, Date.now()),
          log: [logOnly(eventId, 'Desistiu do pedido de troca'), ...s.log],
        }));
        remote?.cancelSwap(eventId, swapId);
        showToast('Pedido de troca cancelado. Você continua escalado.');
      },
      answerSwap: (eventId, swapId, accept) => {
        const swap = swapById(eventId, swapId);

        if (!swap) return;

        const role = stateRef.current.ministries
          .flatMap((m) => m.roles)
          .find((r) => r.id === swap.roleId)?.name;
        const text = accept
          ? `Aceitou assumir ${role ?? ''} no lugar de ${memberName(swap.fromId)}`
          : `Recusou o pedido de troca de ${memberName(swap.fromId)}`;

        setState((s) => ({
          ...s,
          events: T.answerSwap(s.events, eventId, swapId, accept, Date.now()),
          log: [logOnly(eventId, text), ...s.log],
        }));
        remote?.answerSwap(eventId, swapId, accept);
        showToast(accept ? 'Troca aceita. Agora falta a aprovação do líder.' : 'Pedido de troca recusado.', {
          tone: accept ? 'success' : 'info',
        });
      },
      decideSwap: (eventId, swapId, approve) => {
        const state = stateRef.current;
        const swap = swapById(eventId, swapId);

        if (!swap) return;

        const events = T.decideSwap(
          state.events,
          eventId,
          swapId,
          approve,
          buildLookup(state, today),
          Date.now()
        );
        const after = events.find((e) => e.id === eventId)?.swaps.find((w) => w.id === swapId);
        const pair = `${memberName(swap.fromId)} → ${memberName(swap.toId)}`;
        let text = `Recusou a troca entre ${memberName(swap.fromId)} e ${memberName(swap.toId)}`;
        let message = 'Troca recusada. Os dois membros serão avisados.';

        if (after?.status === 'canceled') {
          text = `Troca cancelada: a escala mudou desde o pedido (${pair})`;
          message = 'A escala mudou desde o pedido. A troca foi cancelada.';
        } else if (approve) {
          text = `Aprovou a troca: ${pair} em ${swapRoleName(eventId, swap.fromId)}`;
          message = 'Troca aprovada e escala atualizada.';
        }

        setState((s) => ({ ...s, events, log: [logOnly(eventId, text), ...s.log] }));
        remote?.decideSwap(eventId, swapId, approve);
        showToast(message, { tone: approve && after?.status === 'approved' ? 'success' : 'info' });
      },
      recordAttendance: (eventId, marks) => {
        setState((s) => ({
          ...s,
          events: T.recordAttendance(s.events, eventId, marks, editableRoles(eventId)),
          log: [logOnly(eventId, 'Check-in registrado'), ...s.log],
        }));
        remote?.recordAttendance(eventId, marks);
        showToast('Check-in salvo. A frequência de cada membro foi atualizada.');
      },
      nudge: (eventId) => {
        const event = stateRef.current.events.find((e) => e.id === eventId);

        if (!event) return;

        const { pending } = presenceSummary(event, editableRoles(eventId));
        const label = `${pending} ${pending === 1 ? 'pendente' : 'pendentes'}`;

        setState((s) => ({
          ...s,
          events: T.markNudged(s.events, eventId, Date.now()),
          log: [logOnly(eventId, `Cobrou ${pending} pendente(s)`), ...s.log],
        }));
        remote?.nudge(eventId);
        showToast(`Cobrança enviada a ${label}.`);
      },
      recordAudience: (eventId, input) => {
        const event = stateRef.current.events.find((e) => e.id === eventId);

        if (!event) return 'O evento não existe mais.';
        const permissions = permissionsOf(
          sessionOf(roleRef.current, meOf(stateRef.current, roleRef.current, worldRef.current).id),
          stateRef.current.ministries
        );

        if (!permissions.recordAudience) return 'Só o Administrador registra o público.';
        if (!audienceOpen(event, today)) return 'O público se registra no dia do evento ou depois.';

        const error = audienceInputError(input);

        if (error) return error;

        const edited = event.audience !== null;

        setState((s) => ({
          ...s,
          events: T.recordAudience(s.events, eventId, input, who(), Date.now()),
          log: [
            logOnly(eventId, edited ? 'Registro de público atualizado' : 'Público do evento registrado'),
            ...s.log,
          ],
        }));
        remote?.recordAudience(eventId, input);
        showToast(
          edited
            ? 'Registro de público atualizado.'
            : 'Público registrado. Os indicadores da Membresia foram atualizados.',
          { tone: 'success' }
        );

        return null;
      },
      whatsapp: (eventId, memberId) => {
        setState((s) => ({
          ...s,
          log: [logOnly(eventId, `Cobrou ${memberName(memberId)} pelo WhatsApp`), ...s.log],
        }));
        remote?.whatsapp(eventId, memberId);
        showToast('WhatsApp aberto com a mensagem pronta. A cobrança ficou no histórico do evento.');
      },
      toggleUnavailableDate: (date) => {
        const me = meOf(stateRef.current, roleRef.current, worldRef.current);
        const next = T.toggleUnavailableDate(me, date, `bo${nextId()}`);
        const marked = next.blackouts.length > me.blackouts.length;

        setState((s) => ({ ...s, members: T.saveMember(s.members, next) }));
        remote?.setUnavailableDate(me.id, date, marked);
        showToast(
          marked
            ? `Anotado: você não pode em ${formatDayMonth(date)}. Você não será escalado nessa data.`
            : `${formatDayMonth(date)} voltou a ficar livre.`,
          // Desfazer é marcar (ou desmarcar) de novo.
          { undo: () => store.toggleUnavailableDate(date) }
        );
      },
      markRead: (ids) => {
        const fresh = ids.filter((id) => !stateRef.current.readNotifications.includes(id));

        setState((s) => ({ ...s, readNotifications: [...new Set([...s.readNotifications, ...ids])] }));
        if (fresh.length) remote?.markRead(fresh);
      },
      setMemberAdmin: (memberId, admin) => {
        setState((s) => ({
          ...s,
          members: s.members.map((m) => (m.id === memberId ? { ...m, admin } : m)),
        }));
      },
      hideOnboarding: () => {
        setState((s) => ({ ...s, onboardingHidden: true }));
        remote?.hideOnboarding();
      },
      generate: (eventIds, options) => {
        if (remote) return remote.generate(eventIds, options);

        const s = stateRef.current;

        return Promise.resolve(generateSchedule(s.events, eventIds, s.members, s.ministries, options));
      },
      logout: () => {
        if (api) void remote?.idle().then(api.logout);
      },
      loadDemoData: async (options) => {
        const loaded = (members: number, ministries: number) =>
          showToast(`Demonstração carregada: ${members} pessoas e ${ministries} ministérios.`, {
            tone: 'success',
          });

        if (remote) {
          if (options?.replace && !(await remote.clearChurchData())) return false;

          const result = await remote.loadDemoData();
          if (result) loaded(result.members, result.ministries);

          return !!result;
        }

        const base = options?.replace ? clearChurchState(stateRef.current) : stateRef.current;

        if (!isChurchEmpty(base)) {
          showToast('A demonstração só entra numa igreja sem dados. Limpe os dados da igreja antes.', {
            tone: 'error',
          });

          return false;
        }

        const next = createChurchDemoState(base, { seed: 'demo', today });
        setState(next);
        setUndoStack([]);
        loaded(next.members.length, next.ministries.length);

        return true;
      },
      clearChurchData: async () => {
        const cleared = 'Dados da igreja apagados. Ficaram os administradores.';

        if (remote) {
          if (await remote.clearChurchData()) showToast(cleared, { tone: 'success' });

          return;
        }
        setState(clearChurchState(stateRef.current));
        setUndoStack([]);
        showToast(cleared, { tone: 'success' });
      },
    };

    return store;
  }, [api, apiOrg, meOf, orgs, remote, today]);

  // Erro de sincronização: o aviso mostra o motivo e o estado volta a ser o do servidor.
  useEffect(() => {
    remote?.setHooks({
      onError: (message) => actions.showToast(message, { tone: 'error' }),
      resync: () => {
        void api?.reload().then(({ state: fresh, versions }) => {
          remote.replaceVersions(versions);
          setUndoStack([]);
          setState(fresh);
        });
      },
    });
  }, [actions, api, remote]);

  const value = useMemo<StoreValue>(() => {
    const me = meOf(state, role, world);
    const session = sessionOf(role, me.id);
    const org = apiOrg ?? world.orgs.find((o) => o.id === world.orgId) ?? SAMPLE_ORG;

    return {
      state,
      session,
      org,
      orgs,
      persona: role,
      guest: !state.members.some((m) => m.id === me.id),
      permissions: permissionsOf(session, state.ministries),
      lookup: buildLookup(state, today),
      today,
      me,
      canUndo: undoStack.length > 0,
      toast,
      actions,
      email: api?.email ?? (me.email || `${normalize(me.name).replace(/\s+/g, '.')}@igreja.org`),
      accounts,
      calendar,
      privacy,
    };
  }, [
    accounts,
    privacy,
    actions,
    apiOrg,
    calendar,
    api,
    meOf,
    orgs,
    role,
    state,
    today,
    toast,
    undoStack.length,
    world,
  ]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext);

  if (!value) {
    throw new Error('useStore precisa estar dentro de <AppStoreProvider>.');
  }

  return value;
}
